/**
 * EntitySuggestionCard Component
 * Preview card showing entity information
 */

import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  Description,
  Person,
  Receipt,
  LocalShipping,
  Assignment,
} from "@mui/icons-material";

/**
 * EntitySuggestionCard Component
 * @param {Object} props
 * @param {Object} props.entity - Entity object
 * @param {Function} [props.onClick] - Click handler
 */
const EntitySuggestionCard = ({ entity, onClick }) => {
  if (!entity) return null;

  // Get icon for entity type
  const getEntityIcon = (entityType) => {
    const iconProps = { fontSize: "small", sx: { mr: 1 } };
    switch (entityType) {
      case "quote":
        return <Description {...iconProps} />;
      case "customer":
        return <Person {...iconProps} />;
      case "invoice":
        return <Receipt {...iconProps} />;
      case "order":
        return <LocalShipping {...iconProps} />;
      case "lead":
        return <Assignment {...iconProps} />;
      default:
        return <Description {...iconProps} />;
    }
  };

  // Get color for entity type
  const getEntityColor = (entityType) => {
    switch (entityType) {
      case "quote":
        return "primary";
      case "customer":
        return "success";
      case "invoice":
        return "warning";
      case "order":
        return "info";
      case "lead":
        return "secondary";
      default:
        return "default";
    }
  };

  const metadata = entity.metadata || {};
  const preview = entity.preview || {};

  return (
    <Card
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { boxShadow: 3 } : {},
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ color: `${getEntityColor(entity.entityType)}.main`, mt: 0.5 }}>
            {getEntityIcon(entity.entityType)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {entity.displayName}
              </Typography>
              <Chip
                label={entity.entityType}
                size="small"
                color={getEntityColor(entity.entityType)}
                sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {entity.subtitle}
            </Typography>
            {preview.route && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Route: {preview.route}
              </Typography>
            )}
            {metadata.amount > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Amount: R {metadata.amount.toLocaleString("en-ZA")}
              </Typography>
            )}
            {metadata.status && (
              <Chip
                label={metadata.status}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EntitySuggestionCard;

